import { Component } from '@theme/component';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

// Cambia de categoría re-renderizando solo categorías y grilla; si el destino no usa el módulo, recarga normal.
class RevistaCategories extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (!window.__moxRevistaPopstate) {
      window.__moxRevistaPopstate = true;
      window.addEventListener('popstate', () => {
        const element = document.querySelector('revista-categories');
        if (element instanceof RevistaCategories) element.render(new URL(window.location.href), false);
        else window.location.reload();
      });
    }
  }

  /** @param {MouseEvent} event */
  navigate(event) {
    const link = /** @type {HTMLAnchorElement} */ (event.target);
    if (!link.href || link.target === '_blank') return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    event.preventDefault();
    const url = new URL(link.href);
    url.hash = '';
    if (url.pathname === window.location.pathname) return;
    this.render(url, true);
  }

  /**
   * @param {URL} url
   * @param {boolean} push
   */
  async render(url, push) {
    const ownSection = this.closest('.shopify-section');
    const grid = document.querySelector('results-list[section-id]');
    const gridId = grid?.getAttribute('section-id');
    if (!ownSection || !gridId) return window.location.assign(url);

    const sectionIds = [ownSection.id.replace(/^shopify-section-/, ''), gridId];
    grid.setAttribute('aria-busy', 'true');

    try {
      const responses = await Promise.all(sectionIds.map((id) => sectionRenderer.getSectionHTML(id, true, new URL(url))));
      const parsed = responses.map((html) => new DOMParser().parseFromString(html, 'text/html'));
      if (parsed.some((doc, i) => !doc.getElementById(`shopify-section-${sectionIds[i]}`))) {
        return window.location.assign(url);
      }

      sectionIds.forEach((id, i) => morphSection(id, responses[i]));
      if (push) history.pushState({ revista: true }, '', url);
    } catch {
      window.location.assign(url);
    } finally {
      document.querySelector('results-list[section-id]')?.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('revista-categories')) customElements.define('revista-categories', RevistaCategories);

// Descarga el PDF en la misma página con % de avance; sin CORS cae a descarga nativa en la misma pestaña.
class RevistaPdfDownload extends Component {
  requiredRefs = ['button', 'progress', 'status'];

  /** @param {MouseEvent} event */
  async download(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (this.hasAttribute('aria-busy')) return;

    const { button } = this.refs;
    const url = /** @type {HTMLAnchorElement} */ (button).href;
    this.setAttribute('aria-busy', 'true');
    this.#setProgress(null, 'Generando catálogo…');

    let response;
    try {
      response = await fetch(url);
      if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);

      const total = Number(response.headers.get('Content-Length')) || 0;
      const reader = response.body.getReader();
      const chunks = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        received += value.length;
        if (total) {
          const percent = Math.min(100, Math.round((received / total) * 100));
          this.#setProgress(percent, `Descargando ${percent} %`);
        } else {
          this.#setProgress(null, `Descargando ${(received / 1048576).toFixed(1)} MB`);
        }
      }

      const blobUrl = URL.createObjectURL(new Blob(chunks, { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = this.dataset.filename || 'catalogo.pdf';
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      this.#setProgress(100, '¡Catálogo descargado!', 'done');
    } catch {
      // Con respuesta = error del servicio; sin respuesta = CORS/red, se delega al navegador.
      if (response) {
        this.#setProgress(100, 'Este catálogo aún no está disponible', 'error');
      } else {
        window.location.assign(url);
        this.#setProgress(100, 'Tu descarga comenzó', 'done');
      }
    } finally {
      this.removeAttribute('aria-busy');
      setTimeout(() => {
        if (!this.hasAttribute('aria-busy')) this.refs.progress.hidden = true;
      }, 5000);
    }
  }

  /**
   * @param {number | null} percent - null = indeterminado
   * @param {string} text
   * @param {'loading' | 'done' | 'error'} [state]
   */
  #setProgress(percent, text, state = 'loading') {
    const { progress, status } = this.refs;
    progress.hidden = false;
    progress.dataset.state = state;
    progress.toggleAttribute('data-indeterminate', percent === null);
    progress.style.setProperty('--mox-pdf-progress', `${percent ?? 0}%`);
    status.textContent = text;
  }
}

if (!customElements.get('revista-pdf-download')) customElements.define('revista-pdf-download', RevistaPdfDownload);
